import * as React from 'react';
import {
    Alert,
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { blueberryTwilightPalette } from '@mui/x-charts/colorPalettes';
import { useTheme } from '@mui/material/styles';
import { useRecordContext, useTranslate } from 'react-admin';
import { buildCampaignDailyReport, CampaignBook, CampaignDailyCounts } from './campaignDailyReport';
import { useCampaignResponses } from './useCampaignResponses';

const formatDay = (date: string) =>
    new Date(`${date}T12:00:00Z`).toLocaleDateString('fi-FI', {
        timeZone: 'Europe/Helsinki',
    });

const CampaignDailyReport = () => {
    const record = useRecordContext<{ guid: string; books?: CampaignBook[] }>();
    const translate = useTranslate();
    const theme = useTheme();
    const { data: responses, error } = useCampaignResponses();
    const report = React.useMemo(
        () => responses ? buildCampaignDailyReport(responses, record?.books || []) : null,
        [responses, record?.books]
    );
    if (error) {
        return <Alert severity="error">{translate('resources.campaign.responses_error')}</Alert>;
    }
    if (!report) return <CircularProgress />;
    if (!report.days.length) {
        return <Alert severity="info">{translate('resources.campaign.daily_report.empty')}</Alert>;
    }

    const bookLabel = (book: CampaignBook) =>
        book.title ? `${book.title} (${book.isbn})` : book.isbn;
    const days = report.days.map(day => formatDay(day.date));
    const bookColors = blueberryTwilightPalette(theme.palette.mode);
    const responseSeries = [
        {
            data: report.days.map(day => day.accepted),
            label: translate('resources.campaign.fields.accepted_count'),
            stack: 'responses',
            color: '#2e7d32',
        },
        {
            data: report.days.map(day => day.rejected),
            label: translate('resources.campaign.fields.rejected_count'),
            stack: 'responses',
            color: '#d32f2f',
        },
    ];
    const countCells = (counts: CampaignDailyCounts) => (
        <>
            <TableCell align="right">{counts.responded}</TableCell>
            <TableCell align="right">{counts.accepted}</TableCell>
            <TableCell align="right">{counts.rejected}</TableCell>
            <TableCell align="right">{counts.selected_books}</TableCell>
            {report.books.map(book => (
                <TableCell key={book.isbn} align="right">{counts.book_counts[book.isbn]}</TableCell>
            ))}
        </>
    );

    return (
        <Box sx={{ width: '100%', minWidth: 0 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
                {translate('resources.campaign.daily_report.description')}
            </Typography>
            <Typography variant="h6">
                {translate('resources.campaign.daily_report.responses')}
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ minWidth: Math.max(500, report.days.length * 60) }}>
                    <BarChart
                        height={320}
                        series={responseSeries}
                        xAxis={[{ data: days, scaleType: 'band' }]}
                        yAxis={[{ tickMinStep: 1 }]}
                    />
                </Box>
            </Box>
            <Typography variant="h6">
                {translate('resources.campaign.daily_report.books')}
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ minWidth: Math.max(500, report.days.length * 60) }}>
                    <BarChart
                        height={320}
                        colors={bookColors}
                        series={report.books.map(book => ({
                            id: book.isbn,
                            data: report.days.map(day => day.book_counts[book.isbn]),
                            label: bookLabel(book),
                            stack: 'books',
                        }))}
                        xAxis={[{ data: days, scaleType: 'band' }]}
                        yAxis={[{ tickMinStep: 1 }]}
                        slotProps={{ legend: { hidden: true } }}
                    />
                </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, my: 2 }}>
                {report.books.map((book, index) => (
                    <Box key={book.isbn} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                            aria-hidden
                            sx={{ width: 12, height: 12, bgcolor: bookColors[index % bookColors.length], flexShrink: 0 }}
                        />
                        <Typography variant="body2">{bookLabel(book)}</Typography>
                    </Box>
                ))}
            </Box>
            <TableContainer sx={{ mt: 2 }}>
                <Table size="small" aria-label={translate('resources.campaign.tabs.daily_report')}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{translate('resources.campaign.daily_report.date')}</TableCell>
                            <TableCell align="right">{translate('resources.campaign.fields.responded_count')}</TableCell>
                            <TableCell align="right">{translate('resources.campaign.fields.accepted_count')}</TableCell>
                            <TableCell align="right">{translate('resources.campaign.fields.rejected_count')}</TableCell>
                            <TableCell align="right">{translate('resources.campaign.daily_report.selected_books')}</TableCell>
                            {report.books.map(book => (
                                <TableCell key={book.isbn} align="right" sx={{ minWidth: 140 }}>
                                    {bookLabel(book)}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {report.days.map(day => (
                            <TableRow key={day.date}>
                                <TableCell component="th" scope="row">{formatDay(day.date)}</TableCell>
                                {countCells(day)}
                            </TableRow>
                        ))}
                    </TableBody>
                    <TableFooter>
                        <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 'bold', color: 'text.primary' } }}>
                            <TableCell>{translate('resources.campaign.daily_report.total')}</TableCell>
                            {countCells(report.totals)}
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default CampaignDailyReport;
